import React, { useEffect, useMemo, useState } from "react";
import { Tooltip } from "@mui/material";
import { Card, CardBody } from "reactstrap";
import { useHistory, useParams } from "react-router";

import { ReactComponent as BackIcon } from "assets/img/svg/BackButton.svg";
import { ReactComponent as ExcellIcon } from "assets/img/svg/excel.svg";

import ServerDataGrid from "views/pages/manage/common/data-grid/ServerDataGrid";
import ALoader from "../../../common/a-loader";
import AButton from "../../../common/form-fields-mui/AButton";
import AInput from "../../../common/formFeilds/customInput/AInput";

import {
  downloadVendorsCsv,
  PAYMENT_HISTORY_INITIAL_FILTERS,
  paymentHistorySearchByOptions,
  searchVendorPsidMapping,
} from "../helper";
import ListFilters from "./ListFilters";

const ViewPaymentRaised = () => {
  const { id, isPaymentRaised } = useParams();
  const history = useHistory();
  const paymentRaisedFlag = isPaymentRaised === "true";

  const [loading, setLoading] = useState(false);
  const [psidList, setPsidList] = useState([]);

  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);

  const [filters, setFilters] = useState(PAYMENT_HISTORY_INITIAL_FILTERS);
  const [filterCount, setFilterCount] = useState(0);

  const [userSearchInput, setUserSearchInput] = useState("");
  const [selected, setSelected] = useState("psid");

  useEffect(() => {
    fetchPsidMapping({
      ...filters,
      searchBy: selected,
      searchByValue: userSearchInput,
    });
  }, [filters, currentPage, pageSize]);

  const fetchPsidMapping = async ({
    request = filters,
    page = currentPage,
  }) => {
    setLoading(true);

    try {
      const response = await searchVendorPsidMapping(
        {
          vendorId: Number(id),
          isPaymentRaised: paymentRaisedFlag,
          ...request,
        },
        page,
        pageSize,
      );

      const list = response?.data?.vendorPsidMappingList || [];
      const total = response?.data?.totalRecords || 0;

      setPsidList(list);
      setTotalRows(total);
    } catch (error) {
      console.error("Error fetching mapping:", error);
      setPsidList([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setLoading(true);

    try {
      await downloadVendorsCsv({
        vendorId: Number(id),
        isPaymentRaised: paymentRaisedFlag,
        filters,
        searchValue: userSearchInput,
      });
    } catch (error) {
      console.error("CSV export failed", error);
    } finally {
      setLoading(false);
    }
  };

  // PSID link logic
  const handlePsidClick = (psid) => {
    window.open(
      history.createHref({
        pathname: `admin/edp/studentDetails/${psid}`,
      }),
      "_blank",
    );
  };

  const columns = useMemo(() => {
    const commonColumns = [
      {
        key: "psid",
        label: "PSID",
        width: 150,
        render: (value) => (
          <a
            href="#"
            className="text-primary text-bold"
            onClick={() => handlePsidClick(value)}
          >
            {value}
          </a>
        ),
      },
      {
        key: "transactionId",
        label: "Payment Order ID",
        width: 150,
      },
      {
        key: "applicationId",
        label: "Application ID",
        width: 150,
      },
      {
        key: "studentName",
        label: "Student Name",
        width: 200,
      },
      {
        key: "programAction",
        label: "Program Action",
        width: 200,
      },
      {
        key: "admissionSource",
        label: "Admission Source",
        width: 200,
        render: (value) => `${value || "-"}`,
      },
      {
        key: "totalRealisedAmount",
        label: "Total Realised Fee",
        width: 150,
        render: (value) => `₹ ${value || 0}`,
      },

      {
        key: "termDispVal",
        label: "Term",
        width: 100,
        render: (value) => `${value || "-"}`,
      },
      {
        key: "businessAreaDispVal",
        label: "BU Code",
        width: 120,
        render: (value) => `${value || "-"}`,
      },
      {
        key: "academicGroupDispValue",
        label: "Stream",
        width: 120,
        render: (value) => `${value || "-"}`,
      },
      {
        key: "academicCareerValue",
        label: "Career",
        width: 120,
        render: (value) => `${value || "-"}`,
      },
      {
        key: "courseId",
        label: "Course ID",
        width: 120,
        render: (value) => `${value || "-"}`,
      },
      {
        key: "courseDescription",
        label: "Course Description",
        width: 260,
        render: (value) => (
          <Tooltip title={value}>
            <div className="ellipsis-text-container">{value || "-"}</div>
          </Tooltip>
        ),
      },

      {
        key: "courseAttribute",
        label: "Course Attribute",
        width: 260,
        render: (value) => (
          <Tooltip title={value}>
            <div className="ellipsis-text-container">{value || "-"}</div>
          </Tooltip>
        ),
      },

      {
        key: "courseAttributeValue",
        label: "Course Attribute Value",
        width: 260,
        render: (value) => (
          <Tooltip title={value}>
            <div className="ellipsis-text-container">{value || "-"}</div>
          </Tooltip>
        ),
      },

      {
        key: "registrationDate",
        label: "Registration Date",
        width: 150,
        render: (value) => (value ? new Date(value).toLocaleDateString() : "-"),
      },
    ];

    return [...commonColumns];
  }, [paymentRaisedFlag]);

  const searchInputHandler = (value = userSearchInput) => {
    const updatedFilters = {
      ...filters,
      searchBy: value ? selected : "",
      searchByValue: value,
    };
    setFilters(updatedFilters);
  };

  const onKeyDownHandler = (e) => {
    if (e.key === "Enter" || e.keyCode === 13) {
      searchInputHandler();
    }
  };

  const handleOnUserInput = (value) => {
    setUserSearchInput(value);
    if (value === "" && value !== filters.searchByValue)
      searchInputHandler(value);
  };

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        flexFlow: "column",
        height: "calc(100vh - 100px)",
      }}
    >
      <Card style={{ flexGrow: 1 }}>
        <CardBody style={{ display: "flex", flexFlow: "column" }}>
          {/* HEADER */}
          <div className="search-match-container d-flex justify-content-between">
            <div className="d-flex align-items-center">
              <BackIcon
                className="mr-2 large-text"
                onClick={() => history.goBack()}
              />

              <div className="heading-3 color-black-60 text-bold">
                {paymentRaisedFlag ? "Payment History" : "Students Mapped List"}
              </div>
            </div>

            {/* RIGHT HEADER ACTIONS */}
            <div className="d-flex justify-content-center">
              <AInput
                placeholder="Search"
                className=""
                value={userSearchInput}
                handler={handleOnUserInput}
                onKeyDown={onKeyDownHandler}
                searchIconClickHandler={() => {
                  searchInputHandler();
                }}
                crossIcon={false}
                searchIcon={true}
                style={{ width: "300px" }}
                customSearchIconStyle={{ left: "5px" }}
                rightCustomIconStyles={{
                  fontSize: "12px",
                  position: "absolute",
                  bottom: "7px",
                  right: "120px",
                  margin: "auto",
                }}
                rightCustomIcon={
                  <select
                    value={selected}
                    onChange={(e) => setSelected(e.target.value)}
                    style={{
                      position: "absolute",
                      bottom: "0",
                      margin: "auto 0",
                      background: "#FFF6E3",
                      color: "#A17217",
                      border: "none",
                    }}
                  >
                    {paymentHistorySearchByOptions.map(({ key, value }) => (
                      <option value={key}>{value}</option>
                    ))}
                  </select>
                }
              />

              <ListFilters
                filters={filters}
                setFilters={setFilters}
                filterCount={filterCount}
                setFilterCount={setFilterCount}
                isLoading={loading}
              />

              <AButton onClick={handleExportCSV}>
                <ExcellIcon />
                Export CSV
              </AButton>
            </div>
          </div>

          {/* TABLE */}
          {loading ? (
            <ALoader />
          ) : (
            <div className="pt-3">
              <ServerDataGrid
                rows={psidList}
                columns={columns}
                rowId={(row) => row.id}
                loading={loading}
                pagination={{
                  page: currentPage + 1,
                  pageSize: pageSize,
                  total: totalRows,
                  totalPages: Math.ceil(totalRows / pageSize) || 1,
                }}
                onPageChange={(newPage) => setCurrentPage(newPage - 1)}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(0);
                }}
                height="500px"
              />
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default ViewPaymentRaised;
